import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCcw, Wrench } from 'lucide-react';

/**
 * Slice 6 FR-6.18 — the two operations repairs that survived the amendment
 * review are administrator tools with a reason and an audit row. Rendered on the
 * Batch Jobs tab (already behind the admin gate); the endpoints are ADMIN-only too.
 */

interface RepairRow {
  id: number; entity_type: string; entity_id: number; action: string; reason: string; actor: string; created_at: string;
}

const ROOT = '/api/credit/abf/round2';

const REPAIRS = [
  { key: 'rebuild-amendment-impact', label: 'Rebuild amendment impact', detail: 'Re-derives the before/after impact rows of an activated or superseded version from its frozen changed terms.' },
  { key: 'reconcile-control-effective-dates', label: 'Reconcile control effective dates', detail: 'Sets every rule, node, covenant, consequence and lender class of a version to the version’s own effective date.' },
];

const errorText = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as { error?: string; message?: string } | undefined;
    return payload?.error || payload?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

const AdminRepairsPanel: React.FC = () => {
  const [rows, setRows] = useState<RepairRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [versionId, setVersionId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<RepairRow[]>(`${ROOT}/admin-repairs`, { params: { _: Date.now() } });
      setRows(response.data);
    } catch (error) {
      setMessage({ tone: 'error', text: errorText(error, 'The repair log could not be loaded.') });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const run = async (key: string, label: string) => {
    const id = Number(versionId);
    if (!Number.isInteger(id) || id <= 0) { setMessage({ tone: 'error', text: 'Enter the facility version id to repair.' }); return; }
    if (!reason.trim()) { setMessage({ tone: 'error', text: 'A reason is required; it is written to the audit log.' }); return; }
    setBusy(key);
    try {
      await axios.post(`${ROOT}/facility-versions/${id}/${key}`, { reason: reason.trim() });
      setMessage({ tone: 'ok', text: `${label} ran on facility version ${id} and was recorded.` });
      setReason('');
      await load();
    } catch (error) {
      setMessage({ tone: 'error', text: errorText(error, `${label} was refused.`) });
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-5" data-testid="admin-repairs-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-300" /> Administrator repairs</h2>
          <p className="text-sm text-slate-400 mt-0.5">Two contract-version repairs kept as administrator tools. Each needs a reason and leaves an audit row; they should rarely be needed now that amendment impact is frozen at staging.</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className="inline-flex items-center gap-1.5 rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[10rem_1fr]">
        <label className="text-xs text-slate-400">Facility version id
          <input value={versionId} onChange={event => setVersionId(event.target.value)} inputMode="numeric" className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="e.g. 18" />
        </label>
        <label className="text-xs text-slate-400">Reason (recorded)
          <input value={reason} onChange={event => setReason(event.target.value)} className="mt-1 w-full rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100" placeholder="Why this repair is being run" />
        </label>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {REPAIRS.map(repair => (
          <div key={repair.key} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
            <div className="text-sm font-semibold text-white">{repair.label}</div>
            <p className="text-xs text-slate-400 mt-1">{repair.detail}</p>
            <button type="button" disabled={busy !== null} onClick={() => void run(repair.key, repair.label)} className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50">
              {busy === repair.key ? 'Running…' : 'Run repair'}
            </button>
          </div>
        ))}
      </div>
      {message && <p className={`mt-3 text-sm ${message.tone === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{message.text}</p>}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-2 py-2 text-left">When</th><th className="px-2 py-2 text-left">Action</th><th className="px-2 py-2 text-left">Version</th><th className="px-2 py-2 text-left">Reason</th><th className="px-2 py-2 text-left">By</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-xs text-slate-500">No administrator repairs recorded.</td></tr>}
            {rows.map(row => (
              <tr key={row.id} className="border-t border-slate-800">
                <td className="px-2 py-2 text-xs text-slate-400 whitespace-nowrap">{new Date(row.created_at).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                <td className="px-2 py-2 text-slate-200">{row.action.replace(/_/g, ' ').toLowerCase()}</td>
                <td className="px-2 py-2 font-mono text-xs text-slate-300">{row.entity_type.toLowerCase()} {row.entity_id}</td>
                <td className="px-2 py-2 text-slate-300">{row.reason}</td>
                <td className="px-2 py-2 text-xs text-slate-300">{row.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminRepairsPanel;
